<template lang="pug">
  app-layout
    v-snackbar(v-model="reauthComplete" color="success")
      div Your Microsoft account has been successfully connected.
    v-layout.pt-2(row wrap)
      v-flex(xs6)
        title-bar {{getTitle}}
      v-flex
        v-layout.pt-2(justify-end)
          v-btn.mr-4(
            v-if="hasAtLeastOneEmailAccount && isAllowedToNewConnections"
            @click='handleConnectNewEmail'
            color='primary'
          )
            v-icon.mr-1(left) mail
            |CONNECT A NEW EMAIL
    div.mr-4.ml-4(v-if="hasAtLeastOneEmailAccount")
      v-layout(column)
        v-flex
          email-account-card.mb-2(
            v-for="account in emailAccounts"
            ref="emailAccountCard"
            :email-account="account" 
            @disconnect-account="onShowModalDisconnectEmail"
            @syncContact="onContactSync"
            @reauthComplete="onReauthComplete"
          )
    v-container.pt-5(v-else)
      v-layout(column)
        v-flex
          div.text-xs-center.mt-4
            div.title Setup your Account for Success
            div.py-5.subtitle Get the most out of LionDesk by automatically syncing your contacts, email conversations and calendar.
        v-flex
          v-layout(wrap row justify-center)
            v-flex.pl-2.pr-1.pt-2(xs12 md5 lg4 xl3)
              v-card.provider-card(@click="handleConnectProvider()")
                v-container.text-xs-center.fill-height
                  v-layout(column wrap align-center justify-center)
                    v-flex(xs3)
                      div.provider-title Select your provider for instant setup
                    v-flex
                      v-layout(row wrap fill-height align-center)
                        div.provider-container.pr-4
                          v-img.mb-3.provider-logo(src="/static/img/g_logo.png" height="60" width="60")
                        div.provider-container.pr-4
                          v-img.mb-3.provider-logo(src="/static/img/outlook_logo.png" height="60" width="60")
                        div.provider-container
                          v-img.mb-3.provider-logo(src="/static/img/microsoft_logo_square.png" height="55" width="55")
                    v-flex(xs2 align-end)
                      div.provider-subtitle.mb-1 Sync your Contacts, Email and Calendar all in one simple process.
            v-flex.pl-2.pr-1.pt-2(xs12 md5 lg4 xl3)
              v-card.provider-card(@click="redirectImportContacts()")
                v-container.text-xs-center.fill-height
                  v-layout(column wrap align-center justify-center)
                    v-flex(xs3)
                      div.provider-title Import via CSV
                    v-flex
                      v-layout(row wrap fill-height align-center)
                        v-icon.csv-upload-icon(color="orange darken-2") cloud_upload
                    v-flex(xs2 align-end)
                      div.provider-subtitle 
                        span Upload a CSV file of your contacts and import them directly inside of LionDesk. 
        v-flex.text-xs-center
          div.pt-4
            v-btn.title-case(@click="redirectDashboard()" flat color="primary") Skip this and go to your Dashboard   
    slide-drawer(
      ref="sliderDrawerConnection"
      v-model="showSliderDrawerConnection"
      :confirm-dismiss="confirmDismiss"
    )
      template(slot="title")
        span Connect My Email Account
      template(slot="content")
        v-layout(v-if="userHasFeatureFlag('nylas-ms-reauth')")
          v-card.mx-2.mt-2
            v-layout(:style="{ 'background-color': '#F1D4D7' }")
              v-flex.pl-2.py-1
                v-icon(class="px-2" medium color='#A64951') error_outline
                span(v-html="text")
        connection-drawer(
          ref="connectionDrawer"
          @input="onFinishConnection"
        )
    slide-drawer(
      ref="sliderDrawerContactSync"
      v-model="showSliderDrawerContactSync"
      :confirm-dismiss="confirmDismissContactSync"
      :default-cancel="false"
    )
      template(slot="content")
        contact-sync-drawer(
          :email="contactSyncEmailSelected"
          ref="contactSyncDrawer"
          @input="confirmDismissContactSync = true"
        )
      template(slot="actions")
        v-btn(flat color="primary" @click="$refs.sliderDrawerContactSync.handleDismiss()") SET UP LATER
    cancel-service-modal(
      ref="cancelServiceModal"
      @confirm-cancel-service="handleDisconnectEmail()"
      title="DISCONNECT EMAIL ACCOUNT",
      confirm-keyword="DISCONNECT",
      text-button-confirm="Disconnect"
    )
      template(slot="content")
        v-layout(row wrap)
          v-card-text
            | Are you sure you want to disconnect your account?
            div.my-2.info-box
              | PLEASE NOTE: After disconnecting your account, we won't be able
              | to sync sent/received emails, contacts or calendar events.
    upgrade-to-email-connection(v-model="showUpgradeDialog")
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import SlideDrawer from '@/Core/components/SlideDrawer';
import toggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import TitleBar from '@/Core/components/TitleBar';
import EmailAccountCard from './EmailAccountCard';
import ConnectionDrawer from './ConnectionDrawer';
import CancelServiceModal from '@/Contacts/components/CancelServiceModal';
import emailAccountTypes from './config/email-account-types';
import ContactSyncDrawer from './components/ContactSync/ContactSyncDrawer';
import UpgradeToEmailConnection from './UpgradeToEmailConnection.vue';
import features from './config/features';


export default {
  components: {
    SlideDrawer,
    TitleBar,
    EmailAccountCard,
    ConnectionDrawer,
    CancelServiceModal,
    ContactSyncDrawer,
    UpgradeToEmailConnection,
  },
  data() {
    return {
      accountEmailSelected: null,
      confirmDismiss: false,
      contactSyncEmailSelected: '',
      confirmDismissContactSync: false,
      showUpgradeDialog: false,
      reauthComplete: false,
      text: "<strong>Update Needed!</strong> To keep using connected email sync in LionDesk, please reconnect your email. This update improves security to better protect your account and data.",
    };
  },
  async created() {
    await this.loadEmailAccounts();
    await this.getFeatures();
  },
  mixins: [toggleSlideDrawer],
  watch: {
    user: {
      handler(userInfo) {
        if (userInfo && this.userHasFeatureFlag('nylas-ms-reauth')) {
          this.openConnectionDrawerOrUpgradePlanPage();
        }
      },
      immediate: true,
    }
  },
  computed: {
    ...mapState({
      emailAccounts: state => state.emailManagement.emailAccounts,
      isShownConnectionDrawer: state => state.emailManagement.isShownConnectionDrawer,
      isShownContactSyncDrawer: state => state.contactSync.isShownContactSyncDrawer,
      user: state => state.userInformation.user,
    }),
    ...mapGetters({
      hasAtLeastOneEmailAccount: 'emailManagement/hasAtLeastOneEmailAccount',
      isAllowedToConnectEmail: 'userInformation/isAllowedToUseNylas',
      userHasFeatureFlag: 'userInformation/userHasFeatureFlag',
    }),
    isAllowedToNewConnections() {
      return this.emailAccounts.length < 2 || this.userHasFeatureFlag('nylas-connections-unlimited');
    },
    showSliderDrawerConnection: {
      get() {
        return this.isShownConnectionDrawer;
      },
      set(isShown) {
        this.setShowConnectionDrawer(isShown);
        if (!isShown) {
          this.setImapHelpSelectedTab(0);
        }
      },
    },
    showSliderDrawerContactSync: {
      get() {
        return this.isShownContactSyncDrawer;
      },
      set(isShown) {
        this.setShowContactSyncDrawer(isShown);
      },
    },
    getTitle() {
      return this.hasAtLeastOneEmailAccount ? 'Email Integrations' : 'Setup Your Account';
    },
  },
  methods: {
    ...mapActions('emailManagement', [
      'loadEmailAccounts',
      'disconnectGmail',
      'disconnectOutlook',
      'disconnectExchange',
      'disconnectIMAP',
      'getFeatures',
    ]),
    ...mapActions('userInformation', ['getUser']),
    ...mapMutations('emailManagement', ['setShowConnectionDrawer']),
    ...mapMutations('contactSync', ['setShowContactSyncDrawer']),
    ...mapMutations('emailManagement', [
      'setImapHelpSelectedTab'
    ]),
    showConnectionDrawer() {
      this.setShowConnectionDrawer(true);
    },
    openConnectionDrawerOrUpgradePlanPage() {
      if (!this.isAllowedToConnectEmail) {
        this.showUpgradeDialog = true;
        return;
      }
      this.showConnectionDrawer();
    },
    handleConnectNewEmail() {
      this.openConnectionDrawerOrUpgradePlanPage();
    },
    handleConnectProvider() {
      this.openConnectionDrawerOrUpgradePlanPage();
    },
    redirectImportContacts() {
      this.$router.push('/contacts/import/process');
    },
    redirectDashboard() {
      this.$router.push('/dashboard');
    },
    onShowModalDisconnectEmail(account) {
      this.accountEmailSelected = account;
      this.$refs.cancelServiceModal.show();
    },
    async handleDisconnectEmail() {
      if (!this.accountEmailSelected) return;

      switch (this.accountEmailSelected.providerId) {
        case emailAccountTypes.gmail.id:
          await this.disconnectGmail(this.accountEmailSelected.id);
          break;
        case emailAccountTypes.outlook.id:
          await this.disconnectOutlook(this.accountEmailSelected.id);
          break;
        case emailAccountTypes.exchange.id:
          await this.disconnectExchange(this.accountEmailSelected.id);
          break;
        case emailAccountTypes.imap.id:
          await this.disconnectIMAP(this.accountEmailSelected.id);
          break;
      }
    },
    onFinishConnection(payload) {
      this.$refs.sliderDrawerConnection.handleDismiss();
      const { email, featureIds } = payload;

      if (featureIds.includes(features.contactSync.id)) {
        this.onContactSync(email);
      }
    },
    onContactSync(email) {
      this.showSliderDrawerContactSync = true;
      this.contactSyncEmailSelected = email;
    },
    onReauthComplete() {
      this.reauthComplete = true;
    }
  },
  async mounted() {
    const isTrue = this.$route.query['open-drawer'] === 'true';
    if (isTrue) {
      await this.getUser();
      this.openConnectionDrawerOrUpgradePlanPage();
    }

  },
};
</script>

<style scoped lang="scss">
.title {
  font-size: 50px !important;
  color: var(--v-primary-base);
}

.subtitle {
  font-size: 17px !important;
  font-weight: bold;
  color: var(--v-background-darken1);
}

.provider-card {
  cursor: pointer;
  border-radius: 10px;
  height: 400px;
}

.provider-container {
  display: inline-block;
}

.provider-title {
  font-size: 26px;
  font-weight: bold;
}

.provider-subtitle {
  font-size: 15px;
  color: var(--v-background-darken1);
}

.provider-logo {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.csv-upload-icon {
  font-size: 80px;
}

.highlighted-card {
  border: solid 2px var(--v-primary-base);
}

.disclaimer {
  font-size: 12px;
}
</style>

